import { toQueryString } from '@va/util/helpers';
import { AiChatbotConversation, AiChatbotConversationQuota } from './ai-assistant.types';
import { aiApiClient } from './aiApiClient';

type RawAiModel = {
  consented: boolean;
  default: boolean;
  model_name: string;
};

export const getAvailableAiModels = async (websiteId: string) => {
  return aiApiClient.get<{ models: RawAiModel[] }>(`/website/${websiteId}/models`);
};

export const addConsentForAiModel = async (websiteId: string, modelName: string | string[]) => {
  return aiApiClient.post(`/website/${websiteId}/consent`, {
    models: Array.isArray(modelName) ? modelName : [modelName],
  });
};

export const removeConsentForAiModel = async (websiteId: string, modelName: string | string[]) => {
  return aiApiClient.delete(`/website/${websiteId}/consent`, {
    models: Array.isArray(modelName) ? modelName : [modelName],
  });
};

export type RawAiChatbotConversation = {
  created_at: string;
  id: string;
  last_updated_at: string;
  selected_model: string;
  title: string;
  user_id: string;
  website_key: string;
  num_questions: number;
};

export type ToolCall = {
  id: string;
  arguments: string;
  function_name: string;
};

export enum MessageRoleEnum {
  user = 'user',
  assistant = 'assistant',
  tool = 'tool',
  developer = 'developer',
}

export type MessageRole =
  MessageRoleEnum.user | MessageRoleEnum.assistant | MessageRoleEnum.tool | MessageRoleEnum.developer;

export type RawAiChatbotMessage = {
  content: string;
  conversation_id: string;
  created_at: string;
  id: string;
  model: string;
  role: MessageRole;
  tool_calls: Array<ToolCall> | null;
  remote_id: string | null;
};

export type AiChatbotMessage = {
  content: string;
  conversationId: string;
  createdAt: string;
  id: string;
  model: string;
  role: MessageRole;
  toolCalls: Array<ToolCall> | null;
  remoteId: string | null;
};

type RawConversationWithMessages = {
  conversation: RawAiChatbotConversation;
  messageHistory: Array<RawAiChatbotMessage>;
};

export type ConversationWithMessages = {
  conversation: AiChatbotConversation;
  messageHistory: Array<AiChatbotMessage>;
};

export const getConversation = async (websiteId: string, conversationId: string) => {
  return aiApiClient.get<RawConversationWithMessages>(`/website/${websiteId}/conversations/${conversationId}`);
};

export const getAllConversations = async (websiteId: string) => {
  return aiApiClient.get<{ conversations: RawAiChatbotConversation[] }>(`/website/${websiteId}/conversations`);
};

export const deleteConversation = async (websiteId: string, conversationId: string) => {
  return aiApiClient.delete(`/website/${websiteId}/conversations/${conversationId}`);
};

export const updateConversationMetadata = async (
  websiteId: string,
  conversationId: string,
  data: { title?: string; selectedModel?: string },
) => {
  return aiApiClient.patch(`/website/${websiteId}/conversations/${conversationId}`, data);
};

export const startNewConversation = async ({
  websiteId,
  query,
  message,
  model,
  locale,
  abortController,
}: {
  query: {
    from: string;
    until: string;
    generic: boolean;
  };
  websiteId: string;
  message: string;
  model: string;
  locale: string;
  abortController: AbortController;
}) => {
  return aiApiClient.postToStream(
    `/website/${websiteId}/conversations?${toQueryString(query)}`,
    {
      content: message,
      model,
      locale,
    },
    {
      signal: abortController.signal,
    },
  );
};

export const sendMessage = async ({
  websiteId,
  query,
  conversationId,
  message,
  model,
  locale,
  abortController,
}: {
  websiteId: string;
  query: {
    from: string;
    until: string;
    generic: boolean;
  };
  conversationId: string;
  message: string;
  model: string;
  locale: string;
  abortController: AbortController;
}) => {
  return aiApiClient.postToStream(
    `/website/${websiteId}/conversations/${conversationId}?${toQueryString(query)}`,
    {
      content: message,
      model,
      locale,
    },
    { signal: abortController.signal },
  );
};

export const getConversationQuota = (websiteId: string) => {
  return aiApiClient.get<AiChatbotConversationQuota>(`/website/${websiteId}/conversation-quota`);
};
